<script lang="ts">
import type { Component, Ref } from 'vue';
import type { CompositePrimaryKey, Filter, GlobalCollectionId, LogicalFilterAND, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { ListingLayoutOption } from './types';

export interface ListingProps {
	showSelectionActions?: boolean;
	canBulkDelete?: boolean;
	canFilter?: boolean;
	canEditDisplayOptions?: boolean;
	collectionId: GlobalCollectionId;
	description?: string;
	title?: string;
	deleteLabels?: {
		button?: string;
		dialog?: {
			title?: string;
			description?: string;
			confirm?: string;
		};
		success: {
			title?: string;
		};
	};
	layoutProps?: Record<string, unknown>;
	viewId?: string;
	displayHasChanges?: boolean;
	sharedDisplayOptions?: Record<string, unknown>;
	layoutDisplayName?: string;
	layoutOptions: ListingLayoutOption[];
}

interface MergedLayoutOptions extends Record<string, unknown> {
	collection: ResolvedMonospaceCollection;
	displayOptions?: Record<string, unknown>;
}
</script>

<script lang="ts" setup>
import { whenever } from '@vueuse/core';
import { computed, markRaw, ref, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { isNullishOrEmpty } from '#layers/base/public';
import { getItemPrimaryKey, isEmptyFilter, toApplicableFilter, useCollection } from '#layers/data-model/public';
import { FilterAdd, FilterBar, provideFilterRoot, provideRelatedItemDrawer } from '#layers/filters/public';
import { useCollectionItemPicker } from '#layers/listing/composables/use-collection-item-picker';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { showConfirmationDialog, useToastResultHandling } from '#layers/ui/public';
import { provideListingContext } from './context';
import ListingDisplayOptions from './ListingDisplayOptions.vue';
import ListingSelectionActions from './ListingSelectionActions.vue';

const props = withDefaults(defineProps<ListingProps>(), {
	canFilter: true,
});

defineEmits<{
	saveForEveryone: [];
	resetView: [];
}>();

const slots = defineSlots<{
	'views'?: () => any;
	'title'?: () => any;
	'description'?: () => any;
	'default': (props: { filter: Filter; searchTerm: string; layoutProps: MergedLayoutOptions }) => any;
	'actions'?: () => any;
	'filter-actions'?: () => any;
}>();

const searchTerm = defineModel<string>('searchTerm', { default: '' });

const displayOptions = defineModel<Record<string, unknown> | undefined>('displayOptions', { default: () => ({}) });
const displayOptionsOpen = defineModel<boolean>('displayOptionsOpen', { default: false });
const layoutId = defineModel<string>('layoutId', { default: '$builtin:LayoutTable' });
const viewName = defineModel<string>('viewName');
const viewIcon = defineModel<string>('viewIcon');

const selection = defineModel<CompositePrimaryKey[]>('selection');

const filter = defineModel<Filter>('filter', { default: () => ({ _and: [] }) });

const { t } = useI18n();
const { handleBulkDelete } = useBulkActionHandlers();

const { collection } = useCollection(() => props.collectionId);

const filterViewActive = ref(false);
const hasActiveFilters = computed(() => !isEmptyFilter(toApplicableFilter(filter.value)));

const LayoutOptionComponent = shallowRef<Component | null>(null);

const mergedLayoutOptions = computed<MergedLayoutOptions>(() => ({
	...props.layoutProps,
	collection: collection.value!,
	displayOptions: displayOptions.value,
}));

const DisplayOptionsComponent = ref<Component | null>(null);

const showHeader = computed(() => props.title || props.description || props.canFilter || !!slots.actions || !!slots.views || LayoutOptionComponent.value);

// A clause without a value and an empty group both prune away, so `hasActiveFilters` stays false
// and the watcher below never reveals the bar for them.
const filterRoot = provideFilterRoot(filter as Ref<LogicalFilterAND>, {
	onAdded: () => filterViewActive.value = true,
});

function toggleFilterView() {
	filterViewActive.value = !filterViewActive.value;
}

provideRelatedItemDrawer(useCollectionItemPicker());

provideListingContext({
	setDisplayOptionsComponent: (component: Component | null) => {
		DisplayOptionsComponent.value = component ? markRaw(component) : null;
	},
	...(props.canFilter
		? {
				addFieldFilter: filterRoot.addFilter,
				addFieldFilterWithValue: filterRoot.addFilterWithValue,
			}
		: {}),
});

whenever(hasActiveFilters, () => filterViewActive.value = true, { immediate: true });

function useBulkActionHandlers() {
	const store = useWorkspaceEntityStore();
	const { onSuccess: deleteSuccess, onError: deleteError } = useToastResultHandling({
		error: {
			title: t('listing.selection.bulk_delete.error'),
		},
	});

	return { handleBulkDelete };

	async function handleBulkDelete() {
		if (isNullishOrEmpty(selection.value)) return;

		const collectionApi = store.$collection(collection.value!.apiName);
		const selectedCount = selection.value.length;

		const confirmed = await showConfirmationDialog({
			props: {
				title: props.deleteLabels?.dialog?.title ?? t('listing.selection.bulk_delete.confirm_title', { n: selectedCount }),
				description: props.deleteLabels?.dialog?.description ?? `${t('listing.selection.bulk_delete.confirm_description_delete_items', { n: selectedCount })}\n${t('listing.selection.bulk_delete.confirm_description_destructive')}`,
				confirm: { color: 'error', label: props.deleteLabels?.dialog?.confirm ?? t('listing.selection.bulk_delete.confirm_button') },
			},
		});

		if (confirmed) {
			try {
				await collectionApi.deleteMany(selection.value.map((item) => collectionApi.getKey(getItemPrimaryKey(item, collection.value!))!));

				deleteSuccess({
					title: props.deleteLabels?.success.title ?? t('listing.selection.bulk_delete.success', { n: selectedCount }),
				});

				selection.value = [];
			}
			catch (error) {
				deleteError(error);
			}
		}
	}
}
</script>

<template>
	<div class="flex h-full flex-1 flex-col overflow-hidden" data-listing>
		<div class="relative">
			<DSecondaryHeader
				v-if="showHeader"
				:title
				:description
				:icon="collection?.meta?.icon ?? undefined"
				:icon-color="collection?.meta?.color ?? undefined"
				bar
			>
				<template v-if="!!slots.title" #title>
					<slot name="title" />
				</template>
				<template v-if="!!slots.description" #description>
					<slot name="description" />
				</template>
				<template v-if="!!slots.views" #title-trailing>
					<slot name="views" />
				</template>
				<template #actions>
					<DSearchInputCollapsible v-model="searchTerm" />

					<template v-if="canFilter && collection">
						<FilterAdd
							v-if="!hasActiveFilters && !filterViewActive"
							:collection
							@add-filter="filterRoot.addFilter"
							@add-group="filterRoot.addGroup"
							@add-condition="filterRoot.addCondition"
						>
							<template #default="{ availableFilters }">
								<DTooltip :text="$t('filters.filter')">
									<DButton
										variant="outline"
										size="sm"
										color="neutral"
										:pressed="filterViewActive"
										icon="lucide:list-filter"
										:aria-label="$t('filters.filter')"
										:disabled="availableFilters.length === 0"
										data-testid="listing-filter-button"
									/>
								</DTooltip>
							</template>
						</FilterAdd>
						<DTooltip v-else :text="$t('filters.filter')">
							<DButton
								variant="outline"
								size="sm"
								color="neutral"
								:pressed="filterViewActive"
								:aria-label="$t('filters.filter')"
								data-testid="listing-filter-button"
								@click="toggleFilterView"
							>
								<template #leading>
									<DChip
										:show="hasActiveFilters"
										inset
										size="sm"
										:ui="{ base: '-me-1 -mt-1 ring-(--ui-bg-elevated)' }"
									>
										<DIcon name="lucide:list-filter" size="16" />
									</DChip>
								</template>
							</DButton>
						</DTooltip>
					</template>

					<ListingDisplayOptions
						v-if="canEditDisplayOptions"
						v-model:layout-id="layoutId"
						v-model:name="viewName"
						v-model:icon="viewIcon"
						v-model:open="displayOptionsOpen"
						:has-changes="displayHasChanges"
						:name-placeholder="layoutDisplayName"
						:layout-options
						@save-for-everyone="$emit('saveForEveryone')"
						@reset="$emit('resetView')"
					>
						<DisplayOptionsComponent
							v-if="DisplayOptionsComponent"
							:key="viewId"
							v-bind="mergedLayoutOptions"
							:shared-display-options="props.sharedDisplayOptions"
							@update:display-options="displayOptions = $event"
						/>
					</ListingDisplayOptions>
					<slot name="actions" />
				</template>
			</DSecondaryHeader>

			<ListingSelectionActions
				:visible="showSelectionActions"
				:selection="selection ?? []"
				:can-bulk-delete
				:delete-label="deleteLabels?.button"
				@clear="selection = []"
				@delete-items="handleBulkDelete"
			/>
		</div>
		<Transition
			enter-from-class="h-0! opacity-0"
			enter-to-class="opacity-100"
			enter-active-class="transition-all motion-reduce:transition-none duration-very-fast"
			leave-from-class="opacity-100"
			leave-to-class="h-0! opacity-0"
			leave-active-class="transition-all motion-reduce:transition-none duration-very-fast"
		>
			<div
				v-if="canFilter && filterViewActive && collection"
				class="flex h-11 shrink-0 items-center justify-between gap-2 overflow-hidden border-b px-page"
				data-listing-header
			>
				<FilterBar :collection />
				<slot name="filter-actions" />
			</div>
		</Transition>
		<slot
			:filter
			:search-term
			:layout-props="mergedLayoutOptions"
		/>
	</div>
</template>
